import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

export function FunTab({ onData }) {
  const { activeMember, members, refreshMembers } = useAuth();
  const [q, setQ] = useState(null);
  const [answer, setAnswer] = useState("");
  const [guesses, setGuesses] = useState({}); // index -> member_id
  const [scored, setScored] = useState(null);

  const load = useCallback(async () => {
    if (!activeMember) return;
    const { data } = await api.get(`/question/today?member_id=${activeMember.id}`);
    setQ(data);
  }, [activeMember]);

  useEffect(() => { load(); }, [load]);

  const submitAnswer = async () => {
    if (!answer.trim()) return toast.error("Write your answer first");
    await api.post("/question/answer", { member_id: activeMember.id, text: answer.trim() });
    setAnswer("");
    await load();
    toast.success("Answer locked in! 🤫");
  };

  const submitGuess = async () => {
    const { data } = await api.post("/question/guess", { member_id: activeMember.id, guesses });
    setScored(data);
    await load();
    await refreshMembers();
    onData?.();
    toast.success(`${data.correct} correct — +${data.points} brownie points! 🍫`);
  };

  if (!activeMember) return <div className="card-tactile p-10 text-center text-slate-400 font-medium">Pick who you are up top to play.</div>;
  if (!q) return <div className="card-tactile p-10 text-center text-slate-400 font-medium">Loading today's question…</div>;

  const authorOf = (idx) => q.answer_authors?.find((a) => a.index === idx)?.member_id;
  const leaderboard = [...members].sort((a, b) => b.brownie_points - a.brownie_points);

  return (
    <div className="grid lg:grid-cols-3 gap-6 items-start">
      <div className="lg:col-span-2 space-y-6">
        <div className="card-tactile p-6 sm:p-8" style={{ background: "linear-gradient(160deg,#EEE7FE,#FDE8F0)" }}>
          <div className="text-sm font-bold uppercase tracking-wide text-[#8B5CF6]">🍫 Daily Family Question</div>
          <h2 className="text-2xl sm:text-3xl font-extrabold mt-2" style={{ fontFamily: "Outfit" }}>{q.question}</h2>
          <div className="text-sm font-bold text-slate-500 mt-3">
            {q.answered_count}/{q.total_members} answered
          </div>
        </div>

        {/* Answer phase */}
        {!q.you_answered && (
          <div className="card-tactile p-6">
            <h3 className="font-bold mb-3" style={{ fontFamily: "Outfit" }}>Your secret answer</h3>
            <textarea data-testid="answer-input" value={answer} onChange={(e) => setAnswer(e.target.value)}
              placeholder="Nobody sees this until everyone has answered…"
              className="w-full px-4 py-3 rounded-xl border-2 border-slate-200 outline-none font-medium min-h-[90px]" />
            <button data-testid="submit-answer-btn" onClick={submitAnswer} className="btn-tactile bg-[#8B5CF6] text-white px-6 py-2.5 mt-3">Lock it in 🤫</button>
          </div>
        )}

        {q.you_answered && !q.all_answered && (
          <div className="card-tactile p-8 text-center">
            <div className="text-4xl mb-2">⏳</div>
            <p className="font-bold">You've answered! Waiting for the rest of the family…</p>
          </div>
        )}

        {/* Guess phase */}
        {q.all_answered && (
          <div className="card-tactile p-6">
            <h3 className="font-bold mb-1" style={{ fontFamily: "Outfit" }}>Guess who said what!</h3>
            <p className="text-slate-500 font-medium text-sm mb-4">+10 brownie points per correct guess.</p>
            <div className="space-y-3">
              {q.answers.map((a) => (
                <div key={a.index} data-testid="guess-answer-row" className="rounded-xl border-2 border-slate-100 p-3">
                  <div className="font-bold mb-2">"{a.text}"</div>
                  <div className="flex flex-wrap gap-1.5">
                    {members.map((m) => {
                      const selected = guesses[a.index] === m.id;
                      const reveal = q.you_guessed || scored;
                      const isCorrect = reveal && authorOf(a.index) === m.id;
                      return (
                        <button key={m.id} disabled={q.you_guessed || scored}
                          onClick={() => setGuesses({ ...guesses, [a.index]: m.id })}
                          className={`text-xs font-bold rounded-full px-3 py-1.5 border-2 transition-all ${
                            isCorrect ? "bg-emerald-500 text-white border-emerald-600"
                            : selected ? "text-white" : "text-slate-600 border-slate-200"}`}
                          style={selected && !isCorrect ? { background: m.color, borderColor: m.color } : {}}>
                          {m.avatar} {m.name}{isCorrect && " ✓"}
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
            {!q.you_guessed && !scored && (
              <button data-testid="submit-guess-btn" onClick={submitGuess}
                disabled={Object.keys(guesses).length < q.answers.length}
                className="btn-tactile bg-[#FF5A8B] text-white px-6 py-2.5 mt-4">Submit my guesses 🎯</button>
            )}
            {(q.you_guessed || scored) && (
              <div className="mt-4 font-bold text-emerald-600">🎉 You already guessed today. Come back tomorrow for a new question!</div>
            )}
          </div>
        )}
      </div>

      <div className="card-tactile p-6">
        <h3 className="font-bold mb-4" style={{ fontFamily: "Outfit" }}>🏆 Brownie Points</h3>
        <div className="space-y-2">
          {leaderboard.map((m, i) => (
            <div key={m.id} className="flex items-center gap-3 rounded-xl border-2 border-slate-100 p-2.5">
              <span className="font-extrabold text-slate-300 w-5">{i + 1}</span>
              <span className="w-8 h-8 rounded-full flex items-center justify-center" style={{ background: m.color + "33" }}>{m.avatar}</span>
              <span className="font-bold flex-1" style={{ color: m.color }}>{m.name}</span>
              <span className="font-extrabold">{m.brownie_points} 🍫</span>
            </div>
          ))}
        </div>
        <p className="text-xs text-slate-400 mt-4 font-medium">Earn brownie points here, then spend 50 to trade away a chore in the My Chores tab!</p>
      </div>
    </div>
  );
}
